import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import type { XeroTenant } from '@/hooks/useXeroConnection';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenants: XeroTenant[];
  currentTenantId: string | null;
  onSelect: (tenantId: string) => Promise<unknown>;
  isSaving: boolean;
  /** Send the user back to Xero when the organisation they want is not listed. */
  onAuthoriseAnother?: () => void;
  isAuthorising?: boolean;
}

/** Pick which Xero organisation this company's books live in (mirrors GoogleAdsAccountPickerDialog). */
export function XeroTenantPickerDialog({
  open,
  onOpenChange,
  tenants,
  currentTenantId,
  onSelect,
  isSaving,
  onAuthoriseAnother,
  isAuthorising,
}: Props) {
  const [picked, setPicked] = useState<string | null>(currentTenantId);

  const handleSave = async () => {
    if (!picked) {
      toast({ title: 'Pick an organisation first', variant: 'destructive' });
      return;
    }
    try {
      const result = (await onSelect(picked)) as
        | { currency_added?: boolean; foreign_currency?: boolean; base_currency?: string | null }
        | undefined;
      toast({
        title: 'Xero organisation updated',
        description: result?.currency_added
          ? 'The currency you invoice in was enabled in that organisation so invoices can be sent.'
          : result?.foreign_currency
            ? `Those books are kept in ${result.base_currency}. Xero converts each invoice at its daily rate.`
            : undefined,
      });
      onOpenChange(false);
    } catch (e) {
      toast({
        title: 'Could not use that organisation',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Choose your Xero organisation</DialogTitle>
          <DialogDescription>
            Your Xero login can access more than one organisation. Pick the one that keeps this
            company's books. Its base currency must match the currency you invoice in.
          </DialogDescription>
          {currentTenantId && (
            <div className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm text-muted-foreground">
              Moving to a different organisation is a clean break. The account and tax mapping is
              cleared, everything already synced is forgotten, anything still queued is abandoned,
              and the old organisation is disconnected from Steddy. Nothing in either set of books
              changes.
            </div>
          )}
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {tenants.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No organisations found on that Xero login. Reconnect with a login that has access to
              the organisation you want to sync.
            </p>
          ) : (
            tenants.map((t) => {
              const isPicked = picked === t.tenant_id;
              const isCurrent = currentTenantId === t.tenant_id;
              return (
                <button
                  key={t.tenant_id}
                  type="button"
                  onClick={() => setPicked(t.tenant_id)}
                  className={`w-full text-left rounded-lg border p-3 transition ${
                    isPicked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.tenant_name || 'Unnamed organisation'}</div>
                      <div className="text-xs text-muted-foreground font-mono truncate">{t.tenant_id}</div>
                    </div>
                    {isCurrent && <Badge variant="secondary">Current</Badge>}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          {onAuthoriseAnother && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onAuthoriseAnother}
              disabled={isSaving || isAuthorising}
              className="sm:mr-auto"
            >
              {isAuthorising ? 'Redirecting...' : 'Authorise another organisation'}
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !picked || picked === currentTenantId || tenants.length === 0}>
            {isSaving ? 'Saving...' : 'Use this organisation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
